import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    collection, doc, getDoc, getDocs, orderBy, query, updateDoc,
} from "firebase/firestore";
import { FiAlertCircle, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { toast } from "sonner";
import { db } from "../../../firebase";
import { useAuth } from "../../../contexts/AuthContext";
import { HomeHeader } from "../../Home/HomeHeader";

const STATUS_META = {
    new:      { label: "New",      bg: "#eff6ff", color: "#1d4ed8" },
    reviewed: { label: "Reviewed", bg: "#fefce8", color: "#92400e" },
    closed:   { label: "Closed",   bg: "#f0fdf4", color: "#166534" },
};
const ALL_STATUSES = ["new", "reviewed", "closed"];

export default function IssueReportsPage() {
    const { user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [isProjectManager, setIsProjectManager] = useState(false);
    const [issues, setIssues] = useState([]);
    const [filter, setFilter] = useState("all");
    const [expanded, setExpanded] = useState(null);
    const [updating, setUpdating] = useState(null);

    const homeLink = isAdmin ? "/admin/home" : "/student/home";

    useEffect(() => {
        const load = async () => {
            if (!user?.email) return;
            setLoading(true);
            try {
                const permSnap = await getDoc(doc(db, "Users", user.email));
                const hasPerm = permSnap.exists() && permSnap.data()?.isProjectManager === true;
                setIsProjectManager(hasPerm);
                if (!hasPerm) { setLoading(false); return; }

                const snap = await getDocs(
                    query(collection(db, "IssueReports"), orderBy("timestamp", "desc"))
                );
                setIssues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error("Failed to load issue reports:", err);
                toast.error("Failed to load issue reports.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user]);

    const handleStatusChange = async (issueId, newStatus) => {
        setUpdating(issueId);
        try {
            await updateDoc(doc(db, "IssueReports", issueId), { status: newStatus });
            setIssues(prev => prev.map(i => i.id === issueId ? { ...i, status: newStatus } : i));
            toast.success(`Marked as "${STATUS_META[newStatus].label}".`);
        } catch (err) {
            console.error("Failed to update status:", err);
            toast.error("Failed to update status.");
        } finally {
            setUpdating(null);
        }
    };

    const filtered = filter === "all" ? issues : issues.filter(i => (i.status || "new") === filter);
    const countFor = (s) => s === "all" ? issues.length : issues.filter(i => (i.status || "new") === s).length;

    if (loading) {
        return (
            <>
                <HomeHeader text="Maintenance" link={homeLink} />
                <PageWrapper><Main>Loading...</Main></PageWrapper>
            </>
        );
    }

    if (!isProjectManager) {
        return (
            <>
                <HomeHeader text="Maintenance" link={homeLink} />
                <PageWrapper>
                    <Main>
                        <AccessCard>
                            <AccessTitle>Access restricted</AccessTitle>
                            <AccessText>
                                Your account does not have project manager permission for this page.
                            </AccessText>
                        </AccessCard>
                    </Main>
                </PageWrapper>
            </>
        );
    }

    return (
        <>
            <HomeHeader text="Maintenance" link={homeLink} />
            <PageWrapper>
                <Main>
                    <HeaderRow>
                        <div>
                            <PageTitle>Issue Reports</PageTitle>
                            <PageSubtitle>View and manage user-submitted issue reports.</PageSubtitle>
                        </div>
                        <TotalBadge>{issues.length} total</TotalBadge>
                    </HeaderRow>

                    <FilterTabs>
                        {["all", ...ALL_STATUSES].map(s => (
                            <FilterTab key={s} $active={filter === s} onClick={() => setFilter(s)}>
                                {s === "all" ? "All" : STATUS_META[s].label}
                                <TabCount $active={filter === s}>{countFor(s)}</TabCount>
                            </FilterTab>
                        ))}
                    </FilterTabs>

                    {filtered.length === 0 ? (
                        <EmptyState>
                            <FiAlertCircle size={32} color="#d1d5db" />
                            <span>No {filter === "all" ? "" : STATUS_META[filter]?.label.toLowerCase() + " "}issues found.</span>
                        </EmptyState>
                    ) : (
                        <IssueList>
                            {filtered.map(issue => {
                                const status = issue.status || "new";
                                const isExpanded = expanded === issue.id;
                                const ts = issue.timestamp?.toDate
                                    ? issue.timestamp.toDate().toLocaleString()
                                    : null;
                                return (
                                    <IssueCard key={issue.id}>
                                        <CardHeader onClick={() => setExpanded(isExpanded ? null : issue.id)}>
                                            <CardHeaderLeft>
                                                <StatusBadge $status={status}>
                                                    {STATUS_META[status]?.label || status}
                                                </StatusBadge>
                                                <CardMeta>
                                                    <MetaEmail>{issue.userEmail}</MetaEmail>
                                                    <MetaUrl>{issue.pageUrl}</MetaUrl>
                                                </CardMeta>
                                            </CardHeaderLeft>
                                            <CardHeaderRight>
                                                {ts && <MetaTime>{ts}</MetaTime>}
                                                {isExpanded
                                                    ? <FiChevronUp size={18} color="#6b7280" />
                                                    : <FiChevronDown size={18} color="#6b7280" />
                                                }
                                            </CardHeaderRight>
                                        </CardHeader>

                                        <Description>{issue.description}</Description>

                                        {isExpanded && (
                                            <DetailSection>
                                                {issue.attachments?.length > 0 && (
                                                    <DetailBlock>
                                                        <DetailLabel>
                                                            Attachments ({issue.attachments.length})
                                                        </DetailLabel>
                                                        <Screenshots>
                                                            {issue.attachments.map((att, i) => (
                                                                att.type?.startsWith('video/')
                                                                    ? (
                                                                        <AttachmentVideo
                                                                            key={i}
                                                                            src={att.url}
                                                                            controls
                                                                            preload="metadata"
                                                                        />
                                                                    ) : (
                                                                        <a key={i} href={att.url} target="_blank" rel="noopener noreferrer">
                                                                            <Screenshot src={att.url} alt={`Attachment ${i + 1}`} />
                                                                        </a>
                                                                    )
                                                            ))}
                                                        </Screenshots>
                                                    </DetailBlock>
                                                )}

                                                {issue.errorLog?.length > 0 && (
                                                    <DetailBlock>
                                                        <DetailLabel>Console Log ({issue.errorLog.length})</DetailLabel>
                                                        <ErrorLog>
                                                            {issue.errorLog.map((e, i) => (
                                                                <ErrorEntry key={i}>
                                                                    <ErrorType $type={e.type}>[{e.type}]</ErrorType> {e.message}
                                                                </ErrorEntry>
                                                            ))}
                                                        </ErrorLog>
                                                    </DetailBlock>
                                                )}

                                                <DetailBlock>
                                                    <DetailLabel>Change Status</DetailLabel>
                                                    <StatusButtons>
                                                        {ALL_STATUSES.map(s => (
                                                            <StatusBtn
                                                                key={s}
                                                                $active={status === s}
                                                                $status={s}
                                                                disabled={status === s || updating === issue.id}
                                                                onClick={() => handleStatusChange(issue.id, s)}
                                                            >
                                                                {STATUS_META[s].label}
                                                            </StatusBtn>
                                                        ))}
                                                    </StatusButtons>
                                                </DetailBlock>
                                            </DetailSection>
                                        )}
                                    </IssueCard>
                                );
                            })}
                        </IssueList>
                    )}
                </Main>
            </PageWrapper>
        </>
    );
}

const PageWrapper = styled.div`
    min-height: calc(100vh - 80px);
    background: #f8fafc;
    padding: 24px;
`;

const Main = styled.main`
    width: min(1100px, 100%);
    margin: 0 auto;
    text-align: left;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
`;

const PageTitle = styled.h1`
    margin: 0;
    color: #111827;
    font-size: 1.75rem;
    line-height: 1.25;
`;

const PageSubtitle = styled.p`
    margin: 6px 0 0;
    color: #6b7280;
    font-size: 0.95rem;
`;

const TotalBadge = styled.span`
    font-size: 0.85rem;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    padding: 4px 12px;
    white-space: nowrap;
`;

const FilterTabs = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0;
`;

const FilterTab = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: ${({ $active }) => ($active ? "700" : "500")};
    color: ${({ $active }) => ($active ? "#111827" : "#6b7280")};
    border-bottom: 2px solid ${({ $active }) => ($active ? "#9333ea" : "transparent")};
    margin-bottom: -1px;
    transition: color 0.15s;

    &:hover {
        color: #111827;
    }
`;

const TabCount = styled.span`
    font-size: 0.78rem;
    background: ${({ $active }) => ($active ? "#f3e8ff" : "#f3f4f6")};
    color: ${({ $active }) => ($active ? "#7c3aed" : "#6b7280")};
    border-radius: 10px;
    padding: 1px 7px;
    font-weight: 600;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 0;
    color: #9ca3af;
    font-size: 0.95rem;
`;

const IssueList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const IssueCard = styled.div`
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background: #f9fafb;
    }
`;

const CardHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const CardHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const StatusBadge = styled.span`
    font-size: 0.75rem;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 12px;
    white-space: nowrap;
    background: ${({ $status }) => STATUS_META[$status]?.bg || "#f3f4f6"};
    color: ${({ $status }) => STATUS_META[$status]?.color || "#374151"};
`;

const CardMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const MetaEmail = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MetaUrl = styled.span`
    font-size: 0.78rem;
    color: #9ca3af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MetaTime = styled.span`
    font-size: 0.8rem;
    color: #9ca3af;
`;

const Description = styled.p`
    margin: 0;
    padding: 0 16px 14px;
    font-size: 0.9rem;
    color: #374151;
    line-height: 1.55;
    white-space: pre-wrap;
`;

const DetailSection = styled.div`
    border-top: 1px solid #f3f4f6;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #fafafa;
`;

const DetailBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DetailLabel = styled.span`
    font-size: 0.8rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const Screenshots = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Screenshot = styled.img`
    width: 100px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover {
        opacity: 0.85;
    }
`;

const AttachmentVideo = styled.video`
    width: 200px;
    height: 120px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #000;
`;

const ErrorLog = styled.div`
    background: #1f2937;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 160px;
    overflow-y: auto;
`;

const ErrorEntry = styled.pre`
    margin: 0;
    font-size: 0.78rem;
    color: #d1d5db;
    white-space: pre-wrap;
    word-break: break-all;
    font-family: monospace;
`;

const LOG_TYPE_COLOR = {
    'console.log': '#60a5fa',
    'console.warn': '#fbbf24',
    'console.error': '#f87171',
    'uncaught_error': '#f87171',
    'unhandledrejection': '#f87171',
};

const ErrorType = styled.span`
    color: ${({ $type }) => LOG_TYPE_COLOR[$type] || '#f87171'};
    font-weight: 600;
`;

const StatusButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const StatusBtn = styled.button`
    padding: 6px 16px;
    border-radius: 9999px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: 1.5px solid ${({ $active, $status }) =>
        $active ? STATUS_META[$status]?.color : "#d1d5db"};
    background: ${({ $active, $status }) =>
        $active ? STATUS_META[$status]?.bg : "#ffffff"};
    color: ${({ $active, $status }) =>
        $active ? STATUS_META[$status]?.color : "#6b7280"};
    transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.1s;

    &:hover:not(:disabled) {
        border-color: ${({ $status }) => STATUS_META[$status]?.color};
        background: ${({ $status }) => STATUS_META[$status]?.bg};
        color: ${({ $status }) => STATUS_META[$status]?.color};
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: ${({ $active }) => ($active ? "1" : "0.5")};
    }
`;

const AccessCard = styled.div`
    max-width: 560px;
    padding: 24px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
`;

const AccessTitle = styled.h1`
    margin: 0 0 8px;
    color: #111827;
    font-size: 1.4rem;
`;

const AccessText = styled.p`
    margin: 0;
    color: #4b5563;
    line-height: 1.55;
`;
